import { useCallback, useEffect, useState } from 'react';

export type PopoverPosition = 'top' | 'bottom' | 'left' | 'right';
export type PopoverAlign = 'start' | 'center' | 'end';

interface PositionStyle {
	top: number;
	left: number;
}

interface UsePopoverPositionOptions {
	anchorEl: HTMLElement | null;
	contentEl: HTMLElement | null;
	open: boolean;
	position?: PopoverPosition;
	align?: PopoverAlign;
	offset?: number;
}

const calculatePosition = (
	anchorRect: DOMRect,
	contentRect: DOMRect,
	position: PopoverPosition,
	align: PopoverAlign,
	offset: number
): PositionStyle => {
	let top = 0;
	let left = 0;

	switch (position) {
		case 'top':
			top = anchorRect.top - contentRect.height - offset;
			break;
		case 'bottom':
			top = anchorRect.bottom + offset;
			break;
		case 'left':
			left = anchorRect.left - contentRect.width - offset;
			break;
		case 'right':
			left = anchorRect.right + offset;
			break;
	}

	if (position === 'top' || position === 'bottom') {
		switch (align) {
			case 'start':
				left = anchorRect.left;
				break;
			case 'center':
				left = anchorRect.left + (anchorRect.width - contentRect.width) / 2;
				break;
			case 'end':
				left = anchorRect.right - contentRect.width;
				break;
		}
	} else {
		switch (align) {
			case 'start':
				top = anchorRect.top;
				break;
			case 'center':
				top = anchorRect.top + (anchorRect.height - contentRect.height) / 2;
				break;
			case 'end':
				top = anchorRect.bottom - contentRect.height;
				break;
		}
	}

	// Viewport boundary clamping
	const padding = 8;
	const vw = window.innerWidth;
	const vh = window.innerHeight;

	if (left < padding) left = padding;
	if (left + contentRect.width > vw - padding) {
		left = vw - contentRect.width - padding;
	}
	if (top < padding) top = padding;
	if (top + contentRect.height > vh - padding) {
		top = vh - contentRect.height - padding;
	}

	return { top, left };
};

export const usePopoverPosition = ({
	anchorEl,
	contentEl,
	open,
	position = 'bottom',
	align = 'end',
	offset = 4
}: UsePopoverPositionOptions) => {
	const [positionStyle, setPositionStyle] = useState<PositionStyle>({ top: 0, left: 0 });
	const [ready, setReady] = useState(false);

	const update = useCallback(() => {
		if (!anchorEl || !contentEl) return;
		const anchorRect = anchorEl.getBoundingClientRect();
		const contentRect = contentEl.getBoundingClientRect();
		setPositionStyle(calculatePosition(anchorRect, contentRect, position, align, offset));
		setReady(true);
	}, [anchorEl, contentEl, position, align, offset]);

	// Reset ready state when popover closes
	useEffect(() => {
		if (!open) {
			setReady(false);
		}
	}, [open]);

	useEffect(() => {
		if (!open || !anchorEl || !contentEl) return;

		// Use rAF so the portal content has painted and getBoundingClientRect
		// returns real dimensions instead of {0,0}.
		const rafId = requestAnimationFrame(() => update());

		const handleUpdate = () => update();
		window.addEventListener('scroll', handleUpdate, true);
		window.addEventListener('resize', handleUpdate);

		const ro = new ResizeObserver(handleUpdate);
		ro.observe(contentEl);

		return () => {
			cancelAnimationFrame(rafId);
			window.removeEventListener('scroll', handleUpdate, true);
			window.removeEventListener('resize', handleUpdate);
			ro.disconnect();
		};
	}, [open, anchorEl, contentEl, update]);

	return { positionStyle, ready };
};
